/**
 * Discovery Engine — filterstate (URL-parameters), reistijd (lokaal) en helpers.
 *
 * Alle filters leven in de querystring zodat een gefilterde catalogus deelbaar is.
 * De postcode van de medewerker staat uitsluitend in localStorage en gaat nooit
 * naar de server; de hemelsbrede afstand wordt in de browser berekend met een
 * meegeleverd postcode-centroïdenbestand (client/src/data/postcode4.json).
 */
import type { AcademyLeervorm, AcademyShift, AcademySortering, AcademyTraining, AcademyTrainingenParams, Wallet } from '../../services/medewerkerApi';

export const LEERVORMEN: AcademyLeervorm[] = ['e_learning', 'workshop', 'opleiding'];
export const SHIFTS: AcademyShift[] = ['dag', 'avond', 'nacht'];
export const SORTERINGEN: AcademySortering[] = ['relevantie', 'populair', 'rating', 'snelst'];
export const SCORES = ['4.5', '4.8'] as const;
export type ScoreDrempel = (typeof SCORES)[number];
/** Reistijd-slider: 5 … 100 km, daarboven "Heel Nederland" (= geen afstandsfilter). */
export const KM_MIN = 5;
export const KM_MAX = 100;
export const KM_STAP = 5;
export const POSTCODE_OPSLAG = 'belagom.academy.postcode4';

export interface AcademyFilters {
  vorm: AcademyLeervorm[];
  shift: AcademyShift[];
  /** ISO-weekdagen 1 (ma) – 7 (zo). */
  dagen: number[];
  budget: boolean;
  score: ScoreDrempel | null;
  wkr: boolean;
  advies: boolean;
  sort: AcademySortering;
  /** null = Heel Nederland. Alleen client-side (privacy-omloop). */
  km: number | null;
}

export const LEGE_FILTERS: AcademyFilters = { vorm: [], shift: [], dagen: [], budget: false, score: null, wkr: false, advies: false, sort: 'relevantie', km: null };

function lijst<T extends string>(raw: string | null, toegestaan: readonly T[]): T[] {
  if (!raw) return [];
  const set = new Set<T>();
  for (const s of raw.split(',')) if ((toegestaan as readonly string[]).includes(s)) set.add(s as T);
  return [...set];
}

export function leesFilters(params: URLSearchParams): AcademyFilters {
  const km = parseInt(params.get('km') ?? '', 10);
  const score = params.get('score');
  const sort = params.get('sort');
  return {
    vorm: lijst(params.get('vorm'), LEERVORMEN),
    shift: lijst(params.get('shift'), SHIFTS),
    dagen: [...new Set((params.get('dagen') ?? '').split(',').map((d) => parseInt(d, 10)).filter((d) => d >= 1 && d <= 7))],
    budget: params.get('budget') === '1',
    score: (SCORES as readonly string[]).includes(score ?? '') ? (score as ScoreDrempel) : null,
    wkr: params.get('wkr') === '1',
    advies: params.get('advies') === '1',
    sort: (SORTERINGEN as string[]).includes(sort ?? '') ? (sort as AcademySortering) : 'relevantie',
    km: Number.isFinite(km) && km >= KM_MIN && km <= KM_MAX ? km : null,
  };
}

/** Filters → URL-parameters (lege waarden verwijderd). */
export function schrijfFilters(f: AcademyFilters): Record<string, string | null> {
  return {
    vorm: f.vorm.length ? f.vorm.join(',') : null,
    shift: f.shift.length ? f.shift.join(',') : null,
    dagen: f.dagen.length ? f.dagen.join(',') : null,
    budget: f.budget ? '1' : null,
    score: f.score,
    wkr: f.wkr ? '1' : null,
    advies: f.advies ? '1' : null,
    sort: f.sort === 'relevantie' ? null : f.sort,
    km: f.km === null ? null : String(f.km),
  };
}

/** Server-filters actief (km telt niet mee: die is client-side). */
export function heeftServerFilters(f: AcademyFilters): boolean {
  return f.vorm.length > 0 || f.shift.length > 0 || f.dagen.length > 0 || f.budget || f.score !== null || f.wkr || f.advies || f.sort !== 'relevantie';
}

export function heeftFilters(f: AcademyFilters): boolean {
  return heeftServerFilters(f) || f.km !== null;
}

export function naarParams(f: AcademyFilters, basis: { hoofdgroep?: string | null; subcategorie?: string | null; zoek?: string | null }): AcademyTrainingenParams {
  const p = schrijfFilters(f);
  return {
    ...basis,
    vorm: p.vorm,
    shift: p.shift,
    dagen: p.dagen,
    budget: f.budget ? '1' : null,
    score: f.score,
    wkr: f.wkr ? '1' : null,
    advies: f.advies ? '1' : null,
    sort: f.sort === 'relevantie' ? null : f.sort,
  };
}

/** WKR-grendel: hobby-ruimte op → filter staat vast aan (disabled state). */
export function wkrGrendelVast(wallet: Wallet | undefined): boolean {
  if (!wallet?.actief) return false;
  if (wallet.potten) return wallet.potten.hobby.resterend <= 0;
  return wallet.hobby_resterend !== null && wallet.hobby_resterend !== undefined && wallet.hobby_resterend <= 0;
}

/** De Reistijd-slider is alleen actief bij (ook) fysieke leervormen. */
export function reistijdActief(f: AcademyFilters): boolean {
  return f.vorm.includes('workshop') || f.vorm.includes('opleiding');
}

// ─── Postcode & afstand (100% lokaal) ────────────────────────────────────────

export function normaliseerPostcode4(input: string): string | null {
  const s = input.replace(/\s+/g, '').slice(0, 4);
  return /^[1-9]\d{3}$/.test(s) ? s : null;
}

export function leesPostcode(): string | null {
  try {
    return normaliseerPostcode4(window.localStorage.getItem(POSTCODE_OPSLAG) ?? '');
  } catch {
    return null;
  }
}

export function bewaarPostcode(pc: string | null) {
  try {
    if (pc) window.localStorage.setItem(POSTCODE_OPSLAG, pc);
    else window.localStorage.removeItem(POSTCODE_OPSLAG);
  } catch {
    /* private modus: niets bewaren */
  }
}

export type PostcodeTabel = Record<string, [number, number]>;

let tabelPromise: Promise<PostcodeTabel> | null = null;
/** Laadt het postcode-centroïdenbestand lazy (aparte chunk, ~100 kB). */
export function laadPostcodeTabel(): Promise<PostcodeTabel> {
  if (!tabelPromise) tabelPromise = import('../../data/postcode4.json').then((m) => (m.default ?? m) as unknown as PostcodeTabel);
  return tabelPromise;
}

/** Hemelsbrede afstand in km (haversine). */
export function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b[0] - a[0]);
  const dLon = rad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Afstand tussen twee 4-cijferige postcodes; null als een van beide onbekend is. */
export function afstandKm(tabel: PostcodeTabel, van: string | null, naar: string | null): number | null {
  if (!van || !naar) return null;
  const a = tabel[van];
  const b = tabel[naar];
  if (!a || !b) return null;
  return Math.round(haversineKm(a, b) * 10) / 10;
}

/**
 * Reistijd-filter: e-learnings blijven altijd zichtbaar; fysieke trainingen alleen
 * binnen `km` van de postcode van de medewerker (onbekende locatie → verborgen).
 */
export function binnenReistijd(t: Pick<AcademyTraining, 'leervorm' | 'locatie_postcode'>, tabel: PostcodeTabel | null, postcode: string | null, km: number | null): boolean {
  if (km === null || !postcode || !tabel) return true;
  if (t.leervorm === 'e_learning') return true;
  const d = afstandKm(tabel, postcode, t.locatie_postcode);
  return d !== null && d <= km;
}
